// A server's settings at /s/:serverId/settings[/:section[/:channelId]], drawn by the Server page so its live room
// connection carries on underneath. Owners and admins get Overview, Members, Invites, Channels, Emoji, Stickers and
// @Bloom (integrations are on the app home: Apps → Integrations); members get a read-only Overview. Below a separator,
// everyone gets Stats: My stats (their own past week) and Channel stats (any text channel's; components/WeekStats.tsx),
// and owners and admins also the server's Overview, Emoji & stickers and Latency (components/ServerStats.tsx).
// Everyone but the owner can leave.
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MAX_AGENT_CAP_USD, SERVER_COLORS, type AgentSummary, type AppManifest, type Ban, type Invite, type Me, type Member, type ServerSummary, SERVER_IMAGE, type ServerImageKind } from "@bloom/shared";
import { api } from "../api.ts";
import type { Room, Snapshot } from "../realtime.ts";
import { SettingRow, SettingsShell, type SettingsSection } from "../components/SettingsShell.tsx";
import { Avatar, BloomMark, initials } from "../components/ui.tsx";
import { InviteModal } from "../components/InviteModal.tsx";
import { ImageCropper } from "../components/ImageCropper.tsx";
import { ChannelSettings } from "../components/ChannelSettings.tsx";
import { ActionMenu, Confirm, type MenuAction } from "../components/Confirm.tsx";
import "../components/server-settings.css";
import { CustomSettings } from "../components/EmojiSettings.tsx";
// Stats pages load with their own chunk, only when opened (nothing of them in the app's first load).
const stats = () => import("../components/ServerStats.tsx");
const StatsOverview = lazy(() => stats().then((m) => ({ default: m.StatsOverview })));
const StatsEmoji = lazy(() => stats().then((m) => ({ default: m.StatsEmoji })));
const StatsLatency = lazy(() => stats().then((m) => ({ default: m.StatsLatency })));
const week = () => import("../components/WeekStats.tsx");
const MyWeekStats = lazy(() => week().then((m) => ({ default: m.MyWeekStats })));
const ChannelStatsPage = lazy(() => week().then((m) => ({ default: m.ChannelStatsPage })));
const statsPage = (page: React.ReactNode) => <Suspense fallback={<p className="muted">Loading…</p>}>{page}</Suspense>;

const roleName = (r: Member["role"]) => r[0]!.toUpperCase() + r.slice(1);
const day = (t: number) => new Date(t).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

export function ServerSettings({
  server,
  me,
  room,
  snap,
  members,
  onMembersChanged,
  onServerChanged,
  section,
  focusChannel,
  currentChannel,
}: {
  server: ServerSummary;
  me: Me;
  room: Room | null;
  snap: Snapshot;
  members: Member[];
  onMembersChanged: () => void;
  /** Re-read /me (name, colour, my role). */
  onServerChanged: () => Promise<unknown>;
  section?: string;
  focusChannel?: string;
  /** The channel Channel stats opens on: the one the person came from (#348). */
  currentChannel?: string;
}) {
  const isAdmin = server.role === "owner" || server.role === "admin";
  const sections: SettingsSection[] = [
    { id: "overview", label: "Overview", render: () => <Overview server={server} isAdmin={isAdmin} members={members} onSaved={onServerChanged} /> },
    ...(isAdmin
      ? [
          { id: "members", label: "Members", hint: String(members.length), render: () => <Members server={server} me={me} members={members} online={snap.online} onChanged={onMembersChanged} onRoleChanged={onServerChanged} /> },
          { id: "invites", label: "Invites", render: () => <Invites server={server} me={me} members={members} /> },
          { id: "channels", label: "Channels", render: () => <ChannelSettings room={room} channels={snap.channels} categories={snap.categories} focusId={focusChannel} apps={snap.apps} bots={snap.bots} names={new Map(members.map((m) => [m.id, m.name]))} /> },
          { id: "emoji", label: "Emoji", hint: String(snap.emojis.length), render: () => <CustomSettings server={server} kind="emoji" members={members} /> },
          { id: "stickers", label: "Stickers", hint: String(snap.stickers.length), render: () => <CustomSettings server={server} kind="sticker" members={members} /> },
          // Costs are for owners (who set the cap) and admins (who can see it); members never see them.
          { id: "bloom", label: "@Bloom", render: () => <BloomBudget server={server} members={members} apps={snap.apps} /> },
        ]
      : []),
    // Stats (#348), below a separator. My stats and Channel stats are for any member (#306): your own past week and any
    // text channel's. The server's own stats are owners and admins only (the Worker checks too). Read when a page
    // opens, never while chatting.
    { id: "my-stats", label: "My stats", group: "stats", groupLabel: "Stats", render: () => statsPage(<MyWeekStats server={server} channels={snap.channels} />) },
    { id: "channel-stats", label: "Channel stats", group: "stats", render: () => statsPage(<ChannelStatsPage server={server} channels={snap.channels} categories={snap.categories} members={members} current={currentChannel} />) },
    ...(isAdmin
      ? [
          { id: "stats", label: "Overview", title: "Stats", group: "stats", render: () => statsPage(<StatsOverview server={server} members={members} channels={snap.channels} />) },
          { id: "stats-emoji", label: "Emoji & stickers", title: "Emoji & sticker stats", group: "stats", render: () => statsPage(<StatsEmoji server={server} members={members} channels={snap.channels} />) },
          { id: "latency", label: "Latency", group: "stats", render: () => statsPage(<StatsLatency server={server} />) },
        ]
      : []),
    ...(server.role !== "owner" ? [{ id: "leave", label: "Leave server", danger: true, group: "leave", render: () => <Leave server={server} onLeft={onServerChanged} /> }] : []),
  ];
  return <SettingsShell title={server.name} backTo={`/s/${server.id}`} sections={sections} initial={sections.some((s) => s.id === section) ? section : undefined} />;
}

function Overview({ server, isAdmin, members, onSaved }: { server: ServerSummary; isAdmin: boolean; members: Member[]; onSaved: () => Promise<unknown> }) {
  const [name, setName] = useState(server.name);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // Renamed elsewhere (another admin, another device): follow it unless there are unsaved edits.
  const [seen, setSeen] = useState(server.name);
  if (seen !== server.name) {
    setSeen(server.name);
    if (name === seen) setName(server.name);
  }
  const owner = members.find((m) => m.role === "owner");
  const patch = async (body: { name?: string; color?: string; membersCanInvite?: boolean; acceptingMembers?: boolean }) => {
    setError(null);
    setSaved(false);
    try {
      await api<ServerSummary>(`/servers/${server.id}`, { method: "PATCH", body });
      await onSaved();
      setSaved(true);
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const dirty = name.trim() !== server.name && !!name.trim();
  return (
    <>
      <div className="ss-preview">
        <span className={`ss-tile${server.icon ? " has-icon" : ""}`} style={{ background: server.color }} aria-hidden="true">
          {server.icon ? <img src={server.icon} alt="" width={48} height={48} /> : initials(server.name)}
        </span>
        <div style={{ minWidth: 0 }}>
          <div className="ss-preview-name">{server.name}</div>
          <div className="muted" style={{ fontSize: 13 }}>
            {members.length} {members.length === 1 ? "member" : "members"}
            {owner && ` · owned by ${owner.name}`}
          </div>
        </div>
      </div>
      <SettingRow label="Server name" help={isAdmin ? "Up to 50 characters." : undefined}>
        {isAdmin ? (
          <form
            className="ss-inline"
            onSubmit={(e) => {
              e.preventDefault();
              if (dirty) void patch({ name: name.trim() });
            }}
          >
            <input className="input ss-name" aria-label="Server name" maxLength={50} value={name} onChange={(e) => setName(e.target.value)} />
            <button className="btn btn-small btn-primary" type="submit" disabled={!dirty}>Save</button>
          </form>
        ) : (
          <span className="ss-value">{server.name}</span>
        )}
      </SettingRow>
      {isAdmin && <ServerImageSetting server={server} kind="icon" onSaved={onSaved} />}
      {isAdmin && <ServerImageSetting server={server} kind="banner" onSaved={onSaved} />}
      <SettingRow label="Colour" help={server.icon ? "Shown behind the server's icon, and on its tile where there's no icon." : "The server's tile in the server list."}>
        {isAdmin ? (
          <div className="ss-swatches" role="radiogroup" aria-label="Server colour">
            {SERVER_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={c.toLowerCase() === server.color.toLowerCase()}
                aria-label={c}
                className="ss-swatch"
                style={{ background: c }}
                onClick={() => c.toLowerCase() !== server.color.toLowerCase() && void patch({ color: c })}
              />
            ))}
          </div>
        ) : (
          <span className="ss-swatch static" style={{ background: server.color }} aria-label={server.color} />
        )}
      </SettingRow>
      <SettingRow
        label="Accept new members"
        help={server.acceptingMembers ? "Anyone with a valid invite can join." : "Closed: every invite link and emailed invite is paused until this is turned back on. Nobody already here is affected."}
      >
        {isAdmin ? (
          <input type="checkbox" className="ss-check" aria-label="Accept new members" checked={server.acceptingMembers} onChange={(e) => void patch({ acceptingMembers: e.target.checked })} />
        ) : (
          <span className="ss-value">{server.acceptingMembers ? "Yes" : "No"}</span>
        )}
      </SettingRow>
      <SettingRow label="Members can invite" help="Let everyone make invite links, not just owners and admins.">
        {isAdmin ? (
          <input type="checkbox" className="ss-check" aria-label="Members can invite" checked={server.membersCanInvite} onChange={(e) => void patch({ membersCanInvite: e.target.checked })} />
        ) : (
          <span className="ss-value">{server.membersCanInvite ? "Yes" : "No"}</span>
        )}
      </SettingRow>
      <SettingRow label="Your role">
        <span className="ss-value">{roleName(server.role)}</span>
      </SettingRow>
      {server.role === "owner" && <p className="setting-help">You own this server. To leave it, first transfer ownership to someone else (Members).</p>}
      {error && <p className="error" role="alert">{error}</p>}
      {saved && !error && <p className="muted" role="status" style={{ fontSize: 13 }}>Saved.</p>}
    </>
  );
}

type Pending =
  | { kind: "remove"; member: Member }
  | { kind: "ban"; member: Member }
  | { kind: "transfer"; member: Member }
  | { kind: "role"; member: Member; role: "admin" | "member" };

function Members({ server, me, members, online, onChanged, onRoleChanged }: { server: ServerSummary; me: Me; members: Member[]; online: Set<string>; onChanged: () => void; onRoleChanged: () => Promise<unknown> }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bans, setBans] = useState<Ban[] | null>(null);
  const banReason = useRef("");
  const owner = server.role === "owner";
  const loadBans = () => api<Ban[]>(`/servers/${server.id}/bans`).then(setBans, () => setBans([]));
  useEffect(() => {
    void loadBans();
  }, [server.id]);
  // Discord's rules: the owner can do anything to anyone else; admins can remove members, not admins or the owner.
  const actionsFor = (m: Member): MenuAction[] => {
    if (m.id === me.id || m.role === "owner") return [];
    const out: MenuAction[] = [];
    if (owner) {
      out.push(m.role === "admin" ? { label: "Remove admin", onSelect: () => void setRole(m, "member") } : { label: "Make admin", onSelect: () => void setRole(m, "admin") });
      out.push({ label: "Transfer ownership", onSelect: () => setPending({ kind: "transfer", member: m }) });
    }
    if (owner || m.role === "member") {
      out.push({ label: `Remove from ${server.name}`, danger: true, onSelect: () => setPending({ kind: "remove", member: m }) });
      out.push({ label: `Ban from ${server.name}`, danger: true, onSelect: () => ((banReason.current = ""), setPending({ kind: "ban", member: m })) });
    }
    return out;
  };
  const setRole = async (m: Member, role: "admin" | "member") => {
    setError(null);
    try {
      await api(`/servers/${server.id}/members/${m.id}`, { method: "PATCH", body: { role } });
      onChanged();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const order = { owner: 0, admin: 1, member: 2 } as const;
  const sorted = [...members].sort((a, b) => order[a.role] - order[b.role] || a.name.localeCompare(b.name));
  return (
    <>
      <p className="setting-help" style={{ margin: "0 0 4px" }}>
        {owner ? "As the owner you can make admins, hand the server over, and remove or ban anyone." : "Admins can remove and ban members. Only the owner changes roles."}
      </p>
      {error && <p className="error" role="alert">{error}</p>}
      <ul className="ss-list" aria-label="Members">
        {sorted.map((m) => (
          <li key={m.id} className="ss-member">
            <Avatar id={m.id} name={m.name} size={32} presence={online.has(m.id) ? "online" : undefined} />
            <div className="ss-member-text">
              <div className="ss-member-name">
                {m.name}
                {m.id === me.id && <span className="muted" style={{ fontWeight: 400 }}> (you)</span>}
              </div>
              <div className="muted ss-member-sub">Joined {day(m.joinedAt)}</div>
            </div>
            {m.role !== "member" && <span className={`ss-role ${m.role}`}>{roleName(m.role)}</span>}
            <ActionMenu label={`Actions for ${m.name}`} actions={actionsFor(m)} />
          </li>
        ))}
      </ul>
      {pending?.kind === "remove" && (
        <Confirm
          title={`Remove ${pending.member.name}?`}
          confirmLabel="Remove"
          danger
          onConfirm={async () => {
            await api(`/servers/${server.id}/members/${pending.member.id}`, { method: "DELETE" });
            onChanged();
            return null;
          }}
          onClose={() => setPending(null)}
        >
          They leave {server.name} and its DMs right away. They can come back with any invite that still works; to keep them out, ban them instead.
        </Confirm>
      )}
      {pending?.kind === "ban" && (
        <Confirm
          title={`Ban ${pending.member.name}?`}
          confirmLabel="Ban"
          danger
          onConfirm={async () => {
            await api(`/servers/${server.id}/bans`, { body: { userId: pending.member.id, reason: banReason.current } });
            onChanged();
            void loadBans();
            return null;
          }}
          onClose={() => setPending(null)}
        >
          <p style={{ margin: "0 0 10px" }}>
            They leave {server.name} and its DMs right away, and can't come back through any invite, even with a new account on the same email,
            until someone lifts the ban here.
          </p>
          <label className="field">
            <span className="setting-help" style={{ margin: 0 }}>Reason (optional, only owners and admins see it)</span>
            <input className="input" maxLength={200} onChange={(e) => (banReason.current = e.target.value)} />
          </label>
        </Confirm>
      )}
      {bans && bans.length > 0 && (
        <>
          <div className="section-label" style={{ padding: "16px 0 4px" }}>BANNED · {bans.length}</div>
          <ul className="ss-list" aria-label="Banned">
            {bans.map((b) => (
              <li key={b.userId} className="ss-member">
                <Avatar id={b.userId} name={b.name} size={32} />
                <div className="ss-member-text">
                  <div className="ss-member-name">{b.name}</div>
                  <div className="muted ss-member-sub">
                    Banned by {b.bannedBy} · {day(b.createdAt)}
                    {b.reason ? ` · ${b.reason}` : ""}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-small"
                  onClick={() =>
                    api(`/servers/${server.id}/bans/${b.userId}`, { method: "DELETE" }).then(
                      () => void loadBans(),
                      (e: Error) => setError(e.message),
                    )
                  }
                >
                  Unban
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      {pending?.kind === "transfer" && (
        <Confirm
          title={`Make ${pending.member.name} the owner?`}
          confirmLabel="Transfer ownership"
          danger
          typeToConfirm={pending.member.name}
          onConfirm={async () => {
            await api(`/servers/${server.id}/transfer`, { body: { userId: pending.member.id } });
            onChanged();
            await onRoleChanged();
            return null;
          }}
          onClose={() => setPending(null)}
        >
          {pending.member.name} will own {server.name}. You stay on as an admin, and only they can make you owner again.
        </Confirm>
      )}
    </>
  );
}

function Invites({ server, me, members }: { server: ServerSummary; me: Me; members: Member[] }) {
  const [invites, setInvites] = useState<Invite[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [making, setMaking] = useState(false);
  const load = () => api<Invite[]>(`/servers/${server.id}/invites`).then(setInvites, (e: Error) => setError(e.message));
  useEffect(() => {
    void load();
  }, [server.id]);
  // Esc closes the invite dialog only, not the settings page behind it.
  useEffect(() => {
    if (!making) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      setMaking(false);
      void load();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [making]);
  const nameOf = (id: string) => members.find((m) => m.id === id)?.name ?? "Someone who left";
  return (
    <>
      <SettingRow label="Invite people" help="Make a link (choose how long it works and how many times), or email invites.">
        <button type="button" className="btn btn-small btn-primary" onClick={() => setMaking(true)}>Create invite</button>
      </SettingRow>
      <div className="section-label" style={{ padding: "16px 0 4px" }}>ACTIVE INVITES{invites ? ` · ${invites.length}` : ""}</div>
      {error && <p className="error" role="alert">{error}</p>}
      {invites && !invites.length && <p className="muted" style={{ fontSize: 13 }}>No active invites.</p>}
      <ul className="ss-list" aria-label="Active invites">
        {invites?.map((inv) => (
          <li key={inv.code} className="ss-invite">
            <div className="ss-member-text">
              <div className="ss-invite-code">{inv.email ?? inv.code}</div>
              <div className="muted ss-member-sub">
                by {nameOf(inv.createdBy)} · {inv.uses}
                {inv.maxUses ? `/${inv.maxUses}` : ""} used · {inv.expiresAt ? `expires ${day(inv.expiresAt)}` : "never expires"}
              </div>
            </div>
            <button
              type="button"
              className="btn btn-small"
              onClick={() =>
                api(`/servers/${server.id}/invites/${inv.code}`, { method: "DELETE" }).then(
                  () => void load(),
                  (e: Error) => setError(e.message),
                )
              }
            >
              Revoke
            </button>
          </li>
        ))}
      </ul>
      {making && (
        <InviteModal
          serverId={server.id}
          serverName={server.name}
          isAdmin
          verified={me.emailVerified}
          email={me.email}
          paused={!server.acceptingMembers}
          onClose={() => {
            setMaking(false);
            void load();
          }}
        />
      )}
    </>
  );
}

function Leave({ server, onLeft }: { server: ServerSummary; onLeft: () => Promise<unknown> }) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  return (
    <>
      <p style={{ marginTop: 0 }}>You'll lose access to {server.name}'s channels and your DMs there. To come back, you'll need a new invite.</p>
      <button type="button" className="btn btn-danger" onClick={() => setConfirming(true)}>Leave {server.name}</button>
      {confirming && (
        <Confirm
          title={`Leave ${server.name}?`}
          confirmLabel="Leave server"
          danger
          onConfirm={async () => {
            await api(`/servers/${server.id}/leave`, { body: {} });
            await onLeft();
            navigate("/", { replace: true });
            return null;
          }}
          onClose={() => setConfirming(false)}
        >
          You'll need a new invite to come back.
        </Confirm>
      )}
    </>
  );
}

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const money = (n: number) => (n > 0 && n < 0.01 ? "<$0.01" : usd.format(n));
const monthName = (m: string) => new Date(`${m}-15T12:00:00Z`).toLocaleDateString(undefined, { month: "long", timeZone: "UTC" });

/** @Bloom's spending this month against the server's cap; the owner changes the cap here. */
function BloomBudget({ server, members, apps }: { server: ServerSummary; members: Member[]; apps: AppManifest[] }) {
  const [info, setInfo] = useState<AgentSummary | null>(null);
  const [cap, setCap] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const load = async () => {
    try {
      const s = await api<AgentSummary>(`/servers/${server.id}/agent`);
      setInfo(s);
      setCap(String(s.capUsd));
    } catch (e) {
      setError((e as Error).message);
    }
  };
  useEffect(() => {
    void load();
  }, [server.id]);
  if (!info) return error ? <p className="error" role="alert">{error}</p> : <p className="muted">Loading…</p>;
  const share = info.capUsd > 0 ? Math.min(1, info.spentUsd / info.capUsd) : 1;
  const level = share >= 1 ? " full" : share >= 0.8 ? " near" : "";
  const nameOf = (id: string) => members.find((m) => m.id === id)?.name ?? "Former member";
  const capNum = Number(cap);
  const dirty = cap.trim() !== "" && Number.isFinite(capNum) && capNum !== info.capUsd;
  const save = async () => {
    setError(null);
    setSaved(false);
    try {
      await api(`/servers/${server.id}/agent`, { method: "PUT", body: { capUsd: capNum } });
      await load();
      setSaved(true);
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <>
      <div className="ss-preview">
        <span className="ss-bloom-mark" aria-hidden="true"><BloomMark size={40} /></span>
        <div style={{ minWidth: 0 }}>
          <div className="ss-preview-name">@Bloom</div>
          <div className="muted" style={{ fontSize: 13 }}>
            {info.configured ? `Answers when mentioned or replied to · ${info.model}` : "Not connected to Claude yet"}
          </div>
        </div>
      </div>
      <div className="ss-spend" aria-label="This month's spending">
        <div className="ss-spend-line">
          <strong>{money(info.spentUsd)}</strong>
          <span className="muted"> of {usd.format(info.capUsd)} in {monthName(info.month)}</span>
          <span className="muted ss-spend-replies">{info.replies} {info.replies === 1 ? "reply" : "replies"}</span>
        </div>
        <div className={`ss-bar${level}`} role="meter" aria-label="Budget used" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(share * 100)}>
          <span style={{ width: `${share * 100}%` }} />
        </div>
        <p className="setting-help" style={{ margin: "6px 0 0" }}>
          {share >= 1 ? "The budget is used up: @Bloom (and blooming apps) is paused until next month." : "A hard limit for @Bloom's answers and for building apps: it pauses when it's reached, and starts again on the 1st (UTC)."}
        </p>
      </div>
      {info.topSpenders.length > 0 && (
        <>
          <h3 className="ss-subhead">Who asked most</h3>
          <ul className="ss-list" aria-label="Top spenders">
            {info.topSpenders.map((t) => (
              <li key={t.userId} className="ss-member">
                <Avatar id={t.userId} name={nameOf(t.userId)} size={28} />
                <div className="ss-member-text">
                  <div className="ss-member-name">{nameOf(t.userId)}</div>
                  <div className="muted ss-member-sub">{t.replies} {t.replies === 1 ? "reply" : "replies"}</div>
                </div>
                <span className="ss-value">{money(t.usd)}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {info.byApp?.length > 0 && (
        <>
          <h3 className="ss-subhead">Through apps</h3>
          <ul className="ss-list" aria-label="Spend by app">
            {info.byApp.map((a) => {
              const app = apps.find((x) => x.slug === a.app);
              return (
                <li key={a.app} className="ss-member">
                  <span className="chset-app-icon" aria-hidden="true">{app?.icon ?? "◆"}</span>
                  <div className="ss-member-text">
                    <div className="ss-member-name">{app?.name ?? a.app}</div>
                    <div className="muted ss-member-sub">{a.replies} {a.replies === 1 ? "reply" : "replies"} used it</div>
                  </div>
                  <span className="ss-value">{money(a.usd)}</span>
                </li>
              );
            })}
          </ul>
        </>
      )}
      <SettingRow label="Monthly budget" help={info.canEdit ? `In US dollars, up to $${MAX_AGENT_CAP_USD}. 0 turns @Bloom off.` : "Only the owner can change it."}>
        {info.canEdit ? (
          <form
            className="ss-inline"
            onSubmit={(e) => {
              e.preventDefault();
              if (dirty) void save();
            }}
          >
            <span className="ss-dollar">$</span>
            <input className="input ss-cap" aria-label="Monthly budget in dollars" type="number" inputMode="decimal" min={0} max={MAX_AGENT_CAP_USD} step={1} value={cap} onChange={(e) => setCap(e.target.value)} />
            <button className="btn btn-small btn-primary" type="submit" disabled={!dirty}>Save</button>
          </form>
        ) : (
          <span className="ss-value">{usd.format(info.capUsd)} a month</span>
        )}
      </SettingRow>
      <SettingRow label="Who can bloom apps" help={info.canEdit ? "Blooming builds an app by chatting with Bloom; builds count against the budget above." : "Only the owner can change it."}>
        {info.canEdit ? (
          <span className="card-select ss-who">
            <select
              aria-label="Who can bloom apps"
              value={info.bloomWho}
              onChange={async (e) => {
                setError(null);
                try {
                  await api(`/servers/${server.id}/agent`, { method: "PUT", body: { bloomWho: e.target.value } });
                  await load();
                } catch (err) {
                  setError((err as Error).message);
                }
              }}
            >
              <option value="everyone">Everyone</option>
              <option value="admins">Owners and admins</option>
            </select>
          </span>
        ) : (
          <span className="ss-value">{info.bloomWho === "admins" ? "Owners and admins" : "Everyone"}</span>
        )}
      </SettingRow>
      {error && <p className="error" role="alert">{error}</p>}
      {saved && !error && <p className="muted" role="status" style={{ fontSize: 13 }}>Saved.</p>}
    </>
  );
}

/** Owners and admins: the server's icon (square, in the server switcher) or banner (wide, over the channel list). */
function ServerImageSetting({ server, kind, onSaved }: { server: ServerSummary; kind: ServerImageKind; onSaved: () => Promise<unknown> }) {
  const input = useRef<HTMLInputElement>(null);
  const [cropping, setCropping] = useState<File | null>(null);
  const [state, setState] = useState<{ busy?: boolean; error?: string }>({});
  const current = server[kind];
  const spec = SERVER_IMAGE[kind];
  const icon = kind === "icon";
  const send = async (blob: Blob | null) => {
    setState({ busy: true });
    try {
      if (blob) {
        const res = await fetch(`/api/servers/${server.id}/${kind}`, { method: "PUT", body: blob, headers: { "content-type": blob.type }, credentials: "same-origin" });
        if (!res.ok) throw new Error(((await res.json().catch(() => null)) as { message?: string } | null)?.message ?? "Upload failed.");
      } else await api(`/servers/${server.id}/${kind}`, { method: "DELETE" });
      await onSaved();
      setState({});
    } catch (e) {
      setState({ error: (e as Error).message });
    }
  };
  return (
    <SettingRow label={icon ? "Icon" : "Banner"} help={icon ? "A square picture for the server list, instead of its initials." : "A wide picture at the top of the channel list."}>
      <div className={`ss-image ss-image-${kind}`}>
        {current && <img className={`ss-image-preview ${kind}`} src={current} alt={icon ? "Server icon" : "Server banner"} />}
        <div className="ss-image-actions">
          <button type="button" className="btn btn-small" disabled={state.busy} onClick={() => input.current?.click()}>
            {state.busy ? "Saving…" : current ? "Change" : "Upload"}
          </button>
          {current && (
            <button type="button" className="link-btn us-quiet" disabled={state.busy} onClick={() => void send(null)}>
              Remove
            </button>
          )}
        </div>
        <input
          ref={input}
          type="file"
          accept="image/*"
          hidden
          aria-label={icon ? "Choose an icon" : "Choose a banner"}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) setCropping(f);
          }}
        />
      </div>
      {state.error && <div className="error" role="alert" style={{ fontSize: 13, marginTop: 4 }}>{state.error}</div>}
      {cropping && (
        <ImageCropper
          file={cropping}
          title={icon ? "Crop the icon" : "Crop the banner"}
          view={icon ? { width: 240, height: 240 } : { width: 352, height: 198 }}
          out={{ width: spec.width, height: spec.height }}
          maxBytes={spec.maxBytes}
          onCancel={() => setCropping(null)}
          onDone={(blob) => {
            setCropping(null);
            void send(blob);
          }}
        />
      )}
    </SettingRow>
  );
}
